/**
 * Tujuan: Halaman detail project (data analysis, business, thesis type)
 * Caller: App.js via Route /project/:slug
 * Dependensi: projects data (src/data/projects.js), projectDetail.css
 * Main Functions: ProjectDetail()
 * Side Effects: None
 * Changelog:
 *   2026-09 — Added "thesis" type layout for slug thesis-absa
 */
import React from "react";
import { useParams } from "react-router-dom";
import { Container } from "react-bootstrap";
import { FaLightbulb } from "react-icons/fa";

import projects from "../data/projects";
import "../styles/projectDetail.css";

function ProjectDetail() {
  const { slug } = useParams();
  const project = projects[slug];

  if (!project) {
    return <h2 className="not-found">Project Not Found</h2>;
  }

  // ============ BUSINESS TYPE (TiketYuk) ============
  if (project.type === "business") {
    return (
      <Container fluid className="project-detail-section">
        <Container className="section-container">

          {/* HERO */}
          <div className="project-hero">
            <span className="project-badge business-badge">REAL BUSINESS PROJECT</span>

            <h1>{project.title}</h1>
            <p className="project-hook">{project.hook}</p>

            <div className="project-tags">
              {project.tags.map((tag, i) => (
                <span key={i} className="tag">{tag}</span>
              ))}
            </div>
          </div>

          {/* OVERVIEW (moved to top) */}
          <section className="biz-overview-section">
            <div className="info-card">
              <div className="card-header">
                <span className="icon blue">📄</span>
                <h3>Overview</h3>
              </div>
              <p>{project.overview}</p>
            </div>
          </section>

          {/* BUSINESS HIGHLIGHTS */}
          <section className="biz-highlights">
            <div className="biz-highlights-grid">
              {project.businessHighlights.map((item, i) => (
                <div key={i} className="biz-highlight-card">
                  <span className="biz-highlight-icon">{item.icon}</span>
                  <h4>{item.label}</h4>
                  <p>{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* SERVICES */}
          <section className="biz-services-section">
            <h3 className="section-title">
              <span className="line"></span>
              <span>Our Services</span>
              <span className="line"></span>
            </h3>

            <div className="biz-services-grid">
              {project.services.map((svc, i) => (
                <div key={i} className="biz-service-card">
                  <span className="biz-service-icon">{svc.icon}</span>
                  <h4>{svc.name}</h4>
                  <p>{svc.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* TEAM & MY ROLE */}
          <section className="biz-team-section">
            <div className="top-grid">

              {/* TEAM */}
              <div className="info-card">
                <div className="card-header">
                  <span className="icon yellow">👥</span>
                  <h3>Team Structure</h3>
                </div>
                <div className="biz-team-list">
                  {project.team.map((t, i) => (
                    <div key={i} className="biz-team-item-detail">
                      <div className="biz-team-item-header">
                        <span className="biz-team-role">{t.role}</span>
                        <span className="biz-team-count">{t.count} members</span>
                      </div>
                      {t.tasks && (
                        <ul className="biz-team-tasks">
                          {t.tasks.map((task, j) => (
                            <li key={j}>{task}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* MY ROLE */}
              <div className="info-card biz-role-card">
                <div className="card-header">
                  <span className="icon green">👤</span>
                  <h3>My Role — <span className="highlight">{project.myRole.position}</span></h3>
                </div>
                <ul className="biz-jobdesk-list">
                  {project.myRole.jobdesk.map((job, i) => (
                    <li key={i}>{job}</li>
                  ))}
                </ul>

                {/* COMPETENCIES */}
                {project.myRole.competencies && (
                  <div className="biz-competencies">
                    <p className="biz-competencies-label">Through these responsibilities, I combined:</p>
                    <div className="biz-competencies-grid">
                      {project.myRole.competencies.map((c, i) => (
                        <span key={i} className="biz-competency-tag">
                          {c.icon} {c.label}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

            </div>
          </section>

          {/* ORDERING SYSTEM */}
          <section className="biz-ordering-section">
            <h3 className="section-title">
              <span className="line"></span>
              <span>🌐 {project.orderingSystem.title}</span>
              <span className="line"></span>
            </h3>

            <div className="biz-ordering-card">
              <p className="biz-ordering-desc">{project.orderingSystem.desc}</p>

              <div className="biz-ordering-features">
                <h4>✨ Key Features</h4>
                <ul>
                  {project.orderingSystem.features.map((f, i) => (
                    <li key={i}>{f}</li>
                  ))}
                </ul>
              </div>

              {/* TECH FLOW */}
              <div className="biz-tech-flow">
                <h4>⚙️ Technical Flow</h4>
                <div className="biz-flow-steps">
                  {project.orderingSystem.techFlow.map((flow, i) => (
                    <React.Fragment key={i}>
                      <div className="biz-flow-step">
                        <span className="biz-flow-step-num">{String(i + 1).padStart(2, "0")}</span>
                        <h5>{flow.step}</h5>
                        <p>{flow.desc}</p>
                      </div>
                      {i < project.orderingSystem.techFlow.length - 1 && (
                        <div className="biz-flow-arrow">→</div>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* INSIGHTS */}
          <section>
            <h3 className="section-title">Key Insights</h3>
            <div className="insight-grid">
              {project.insights.map((text, index) => (
                <div className="insight-card" key={index}>
                  <div className="insight-icon">
                    {["🎯", "📱", "🤖", "📊"][index]}
                  </div>
                  <span className="insight-number">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <p>{text}</p>
                </div>
              ))}
            </div>
          </section>

          {/* IMPACT */}
          <section className="business-impact">
            <h3 className="section-title">Business Impact</h3>
            <div className="impact-grid">
              {project.impact.map((text, index) => (
                <div key={index} className="impact-card">
                  <div className={`impact-icon icon-${index}`}>
                    {["🎫", "⚡", "🔔", "🌟"][index]}
                  </div>
                  <p>{text}</p>
                </div>
              ))}
            </div>
          </section>

          {/* TOOLS & LINKS */}
          <section className="tools-links-section">
            <div className="tools-links-grid">

              {/* TOOLS */}
              <div className="tools-section">
                <h3>
                  <span className="section-icon">⚙️</span>
                  Tools & Technologies
                </h3>
                <div className="tools">
                  {project.tags.map((tag, i) => (
                    <span key={i} className="tool-tag">{tag}</span>
                  ))}
                </div>
              </div>

              {/* DIVIDER */}
              <div className="vertical-divider"></div>

              {/* LINKS */}
              <div className="links-section">
                <h3>
                  <span className="section-icon">🔗</span>
                  Project Links
                </h3>
                <div className="project-links">
                  {project.website && (
                    <a href={project.website} target="_blank" rel="noreferrer">
                      <div className="link-card website-link">
                        <span>Visit TiketYuk Website</span>
                        <span>→</span>
                      </div>
                    </a>
                  )}
                </div>

                {/* SOCIAL MEDIA */}
                {project.socials && (
                  <div className="biz-socials">
                    <p className="biz-socials-label">Social Media</p>
                    <div className="biz-socials-grid">
                      <a href={project.socials.x} target="_blank" rel="noreferrer" className="biz-social-btn">
                        X (Twitter)
                      </a>
                      <a href={project.socials.instagram} target="_blank" rel="noreferrer" className="biz-social-btn">
                        Instagram
                      </a>
                      <a href={project.socials.tiktok} target="_blank" rel="noreferrer" className="biz-social-btn">
                        TikTok
                      </a>
                    </div>
                  </div>
                )}
              </div>

            </div>
          </section>

          {/* TAKEAWAY */}
          <section className="takeaway">
            <div className="takeaway-card">
              <span className="takeaway-icon">💡</span>
              <div>
                <h4>Key Takeaway</h4>
                <p className="takeaway-text">{project.takeaway}</p>
              </div>
            </div>
          </section>

        </Container>
      </Container>
    );
  }

  // ============ THESIS TYPE ============
  if (project.type === "thesis") {
    return (
      <Container fluid className="project-detail-section">
        <Container className="section-container">

          {/* HERO */}
          <div className="project-hero">
            <span className="project-badge thesis-badge">MASTER'S THESIS — IN PROGRESS</span>
            <h1>{project.title}</h1>
            <p className="project-hook">{project.hook}</p>
            <div className="project-tags">
              {project.tags.map((tag, i) => (
                <span key={i} className="tag">{tag}</span>
              ))}
            </div>
          </div>

          {/* OVERVIEW + BACKGROUND */}
          <div className="top-grid">
            <div className="info-card">
              <div className="card-header">
                <span className="icon blue">📄</span>
                <h3>Overview Penelitian</h3>
              </div>
              <p>{project.overview}</p>
            </div>
            <div className="info-card">
              <div className="card-header">
                <span className="icon yellow">📚</span>
                <h3>Latar Belakang</h3>
              </div>
              <p>{project.background}</p>
            </div>
          </div>

          {/* RESEARCH QUESTIONS */}
          <section>
            <h3 className="section-title">Rumusan Masalah</h3>
            <div className="thesis-rq-list">
              {project.researchQuestions.map((rq, i) => (
                <div key={i} className="thesis-rq-item">
                  <span className="thesis-rq-num">RQ{i + 1}</span>
                  <p>{rq}</p>
                </div>
              ))}
            </div>
          </section>

          {/* ASPECT CATEGORIES */}
          <section>
            <h3 className="section-title">Kategori Aspek Sentimen</h3>
            <div className="thesis-aspect-grid">
              {project.aspects.map((asp, i) => (
                <div key={i} className="thesis-aspect-card">
                  <span className="thesis-aspect-icon">{asp.icon}</span>
                  <h4>{asp.name}</h4>
                  <p className="thesis-aspect-desc">{asp.desc}</p>
                  {asp.keywords && (
                    <div className="thesis-aspect-keywords">
                      {asp.keywords.map((kw, j) => (
                        <span key={j} className="thesis-keyword-tag">{kw}</span>
                      ))}
                    </div>
                  )}
                  <div className="thesis-aspect-example">
                    <span className="thesis-example-label">Contoh ulasan:</span>
                    <em>{asp.example}</em>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* METHODOLOGY PIPELINE */}
          <section>
            <h3 className="section-title">Alur Metodologi</h3>
            <div className="thesis-pipeline">
              {project.pipeline.map((step, i) => (
                <React.Fragment key={i}>
                  <div className="thesis-pipeline-step">
                    <span className="thesis-pipeline-icon">{step.icon}</span>
                    <span className="thesis-pipeline-num">{String(i + 1).padStart(2, "0")}</span>
                    <h5>{step.step}</h5>
                    <p>{step.desc}</p>
                  </div>
                  {i < project.pipeline.length - 1 && (
                    <div className="thesis-pipeline-arrow">→</div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </section>

          {/* MODEL COMPARISON */}
          <section>
            <h3 className="section-title">Perbandingan Model</h3>
            <div className="thesis-model-grid">
              {project.modelComparison.map((model, i) => (
                <div key={i} className="thesis-model-card">
                  <h4 className="thesis-model-name">{model.model}</h4>
                  {model.modelId && (
                    <code className="thesis-model-id">{model.modelId}</code>
                  )}
                  <p className="thesis-model-desc">{model.desc}</p>
                  <div className="thesis-model-pros">
                    <span className="thesis-model-label pros">✔ Kelebihan</span>
                    <ul>
                      {model.pros.map((pro, j) => <li key={j}>{pro}</li>)}
                    </ul>
                  </div>
                  <div className="thesis-model-cons">
                    <span className="thesis-model-label cons">✖ Keterbatasan</span>
                    <ul>
                      {model.cons.map((con, j) => <li key={j}>{con}</li>)}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* FINE-TUNING PARAMETERS */}
          {project.finetuningParams && (
            <section className="thesis-ft-section">
              <h3 className="section-title">Parameter Fine-Tuning</h3>
              <div className="thesis-ft-table-wrap">
                <table className="thesis-ft-table">
                  <thead>
                    <tr>
                      <th>Parameter</th>
                      <th>Nilai</th>
                    </tr>
                  </thead>
                  <tbody>
                    {project.finetuningParams.map((row, i) => (
                      <tr key={i}>
                        <td>{row.param}</td>
                        <td><code>{row.value}</code></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* RESULTS TABLE — PLACEHOLDER */}
          <section className="thesis-results-section">
            <h3 className="section-title">Hasil Eksperimen</h3>
            <div className="thesis-results-note">
              <span>⚠️</span>
              <p>{project.results.note}</p>
            </div>
            <div className="thesis-results-grid">
              <div className="thesis-results-model-block">
                <h4>IndoBERT — Overall F1: <span className="tbd-badge">{project.results.indobert.overall_f1}</span></h4>
                <table className="thesis-results-table">
                  <thead>
                    <tr>
                      <th>Aspek</th>
                      <th>F1-Score</th>
                      <th>Accuracy</th>
                    </tr>
                  </thead>
                  <tbody>
                    {project.results.indobert.perAspect.map((row, i) => (
                      <tr key={i}>
                        <td>{row.aspect}</td>
                        <td><span className="tbd-badge">{row.f1}</span></td>
                        <td><span className="tbd-badge">{row.accuracy}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="thesis-results-model-block">
                <h4>IndoRoBERTa — Overall F1: <span className="tbd-badge">{project.results.indoroberta.overall_f1}</span></h4>
                <table className="thesis-results-table">
                  <thead>
                    <tr>
                      <th>Aspek</th>
                      <th>F1-Score</th>
                      <th>Accuracy</th>
                    </tr>
                  </thead>
                  <tbody>
                    {project.results.indoroberta.perAspect.map((row, i) => (
                      <tr key={i}>
                        <td>{row.aspect}</td>
                        <td><span className="tbd-badge">{row.f1}</span></td>
                        <td><span className="tbd-badge">{row.accuracy}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          {/* TOOLS */}
          <section className="tools-links-section">
            <div className="tools-section">
              <h3>
                <span className="section-icon">⚙️</span>
                Tools & Technologies
              </h3>
              <div className="tools">
                {project.tools.map((tool, i) => (
                  <span key={i} className="tool-tag">{tool}</span>
                ))}
              </div>
            </div>
          </section>

          {/* TAKEAWAY */}
          <section className="takeaway">
            <div className="takeaway-card">
              <span className="takeaway-icon">💡</span>
              <div>
                <h4>Key Takeaway</h4>
                <p className="takeaway-text">{project.takeaway}</p>
              </div>
            </div>
          </section>

        </Container>
      </Container>
    );
  }

  // ============ DATA ANALYSIS TYPE ============
  // Neutral icons for insight cards — works across all data project types
  const insightIcons = ["🔍", "📊", "🌐", "📈"];
  const impactIcons = ["🎯", "🖥️", "📊", "📈"];

  return (
    <Container fluid className="project-detail-section">
      <Container className="section-container">

        {/* HERO */}
        <div className="project-hero">
          <span className="project-badge">DATA ANALYSIS PROJECT</span>

          <h1>{project.title}</h1>
          <p className="project-hook">{project.hook}</p>

          <div className="project-tags">
            {project.tags.map((tag, i) => (
              <span key={i} className="tag">{tag}</span>
            ))}
          </div>
        </div>

        {/* OVERVIEW + EXECUTIVE */}
        <div className="top-grid">
          <div className="info-card">
            <div className="card-header">
              <span className="icon blue">📄</span>
              <h3>Overview</h3>
            </div>
            <p>{project.overview}</p>
          </div>

          <div className="info-card">
            <div className="card-header">
              <span className="icon yellow">⭐</span>
              <h3>Executive Summary</h3>
            </div>
            <p>{project.executive}</p>
          </div>
        </div>

        {/* INSIGHTS */}
        <section>
          <h3 className="section-title">Key Insights</h3>

          <div className="insight-grid">
            {project.insights.map((text, index) => (
              <div className="insight-card" key={index}>
                <div className="insight-icon">
                  {insightIcons[index]}
                </div>
                <span className="insight-number">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <p>{text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ================== KEY VISUALS ================== */}

        <section>
          <h3 className="section-title">Key Visuals</h3>

          <div className="visual-grid">

            {/* CHART 1 */}
            <div className="visual-card">
              <img
                src={project.images.genre || project.images.chart1}
                alt="chart 1"
              />
            </div>

            <div className="insight-box blue">
              <div className="insight-title">
                <FaLightbulb /> Insight
              </div>
              <ul>
                {project.visualInsights.chart1.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            </div>

            {/* CHART 2 */}
            <div className="visual-card">
              <img
                src={project.images.platform || project.images.chart2}
                alt="chart 2"
              />
            </div>

            <div className="insight-box green">
              <div className="insight-title">
                <FaLightbulb /> Insight
              </div>
              <ul>
                {project.visualInsights.chart2.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
              {slug === "video-game-sales" && (
                <p className="insight-footer">
                  → Each region has distinct platform preferences
                </p>
              )}
            </div>

          </div>
        </section>

        {/* DATA WAREHOUSE SECTION */}
        {slug === "thelook-dwh" && (
          <section className="warehouse-section">

            <div className="section-title">
              <span className="line"></span>
              <h3>Data Warehouse Design</h3>
              <span className="line"></span>
            </div>

            <div className="warehouse-card">
              <p>
                This project implements a star schema data warehouse consisting of
                fact and dimension tables to support scalable analytics and SQL-based querying.
              </p>

              <ul>
                <li><b>Fact_Sales</b> → transactional revenue data</li>
                <li><b>Dim_User</b> → customer information</li>
                <li><b>Dim_Product</b> → product attributes</li>
                <li><b>Dim_Date</b> → time-based analysis</li>
              </ul>

              <pre className="sql-box">
                {`SELECT p.category, SUM(f.revenue)
        FROM fact_sales f
        JOIN dim_product p ON f.product_id = p.product_id
        GROUP BY p.category;`}
              </pre>
            </div>

          </section>
        )}

        {/* DASHBOARD */}
        {project.demo && (
          <section className="dashboard-section">

            <div className="section-title">
              <span className="line"></span>
              <h3>Dashboard</h3>
              <span className="line"></span>
            </div>

            <div className="dashboard-card">

              <img
                src={project.images.dashboard}
                alt="dashboard preview"
                className="dashboard-preview"
              />

            </div>
          </section>
        )}

        {/* IMPACT */}
        <section className="business-impact">
          <h3 className="section-title">Business Impact</h3>

          <div className="impact-grid">
            {project.impact.map((text, index) => (
              <div key={index} className="impact-card">

                <div className={`impact-icon icon-${index}`}>
                  {impactIcons[index]}
                </div>

                <p>{text}</p>

              </div>
            ))}
          </div>
        </section>

        {/* TOOLS & LINKS */}
        <section className="tools-links-section">

          <div className="tools-links-grid">

            {/* TOOLS */}
            <div className="tools-section">
              <h3>
                <span className="section-icon">⚙️</span>
                Tools & Technologies
              </h3>

              <div className="tools">
                {project.tags.map((tag, i) => (
                  <span key={i} className="tool-tag">
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            {/* DIVIDER */}
            <div className="vertical-divider"></div>

            {/* LINKS */}
            <div className="links-section">
              <h3>
                <span className="section-icon">🔗</span>
                Project Links
              </h3>

              <div className="project-links">

                <a href={project.github} target="_blank" rel="noreferrer">
                  <div className="link-card github">
                    <span>GitHub Repository</span>
                    <span>→</span>
                  </div>
                </a>

                <a href={project.demo} target="_blank" rel="noreferrer">
                  <div className="link-card tableau">
                    <span>Live Dashboard (Tableau)</span>
                    <span>→</span>
                  </div>
                </a>

              </div>
            </div>

          </div>
        </section>

        {/* TAKEAWAY */}
        <section className="takeaway">
          <div className="takeaway-card">
            <span className="takeaway-icon">💡</span>

            <div>
              <h4>Key Takeaway</h4>
              <p className="takeaway-text">{project.takeaway}</p>
            </div>
          </div>
        </section>

      </Container>
    </Container>
  );
}

export default ProjectDetail;
